import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Form, Modal, ToggleButton, ToggleButtonGroup } from "react-bootstrap";

// ScheduleStoryIdeaModal — single, reusable modal for picking a
// publication time. Used in two places (#3121):
//
//   1. The story_ideas index row action — admin clicks Schedule on a
//      draft row.
//   2. The story_idea form footer — admin clicks Schedule while
//      creating/editing a draft.
//
// Both surfaces want the same affordance: a small set of presets for
// the common cases (10 min, 30 min, 1 hour, 2 hours) plus an explicit
// custom datetime for everything else. Keeping it one component means
// the UX stays consistent across surfaces.
//
// Interaction: the preset buttons SELECT (highlight) the option; the
// admin then confirms with the footer Schedule button. This mirrors
// the standard "pick option, confirm" pattern and avoids the surprise
// of an immediate fire-on-click on the presets. Custom expands an
// inline datetime input with a humanised "Publishes <day, time>"
// preview; the same footer Schedule confirms either choice.
//
// onSchedule is called with either:
//   { publish_in_minutes: <int> }   — for preset durations
//   { publish_at: <ISO8601 string> } — for custom datetime
// The caller fires the HTTP request and closes the modal on success.
// This component validates client-side (custom time ≥ MIN_DELAY_MIN
// from now) and hands the chosen value up.
//
// previousPublishAt pre-fills the custom datetime input when the
// admin reverted a prior schedule. Forward-clamped to
// `max(previousPublishAt, now + MIN_DELAY_MIN)` so a revert-then-
// reopen scenario doesn't submit a stale time the backend would 422
// on (e.g., scheduled for T+10min, reverted at T+5min, modal opened
// at T+8min — the saved value is now only 2 minutes from now).

const MIN_DELAY_MIN = 10;
const PRESET_OPTIONS = [
  { id: "preset-10", label: "In 10 min", minutes: 10 },
  { id: "preset-30", label: "In 30 min", minutes: 30 },
  { id: "preset-60", label: "In 1 hour", minutes: 60 },
  { id: "preset-120", label: "In 2 hours", minutes: 120 },
];
const CUSTOM_ID = "custom";

// Format a Date for an <input type="datetime-local">: YYYY-MM-DDTHH:MM
// in the user's local time. (datetime-local treats values as local
// time; the server interprets via Time.zone.parse — see
// Admin::StoryIdeasController#publish_at_from_params.)
function formatForDatetimeLocal(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function humanizePublishAt(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  const opts = {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  };
  return date.toLocaleString(undefined, opts);
}

function ScheduleStoryIdeaModal({ show, onHide, onSchedule, storyIdeaTitle, previousPublishAt, busy }) {
  // selection is either a preset id ("preset-10" etc.) or "custom".
  // null until the admin picks one.
  const [selection, setSelection] = useState(null);
  const [customDatetime, setCustomDatetime] = useState("");
  const [customError, setCustomError] = useState(null);

  // Reset modal state every time it (re)opens. Without this, a
  // previous error or expanded custom panel persists across opens.
  useEffect(() => {
    if (!show) return;
    setSelection(null);
    setCustomDatetime("");
    setCustomError(null);
  }, [show]);

  const handlePresetSelect = (id) => {
    setSelection(id);
    setCustomError(null);
  };

  const handleCustomSelect = () => {
    setSelection(CUSTOM_ID);
    setCustomError(null);
    if (!customDatetime) {
      const minMs = Date.now() + MIN_DELAY_MIN * 60_000;
      const prevMs = previousPublishAt ? new Date(previousPublishAt).getTime() : 0;
      const initialMs = Math.max(minMs, prevMs);
      setCustomDatetime(formatForDatetimeLocal(new Date(initialMs)));
    }
  };

  const customPreview = useMemo(() => {
    if (!customDatetime) return "";
    const d = new Date(customDatetime);
    if (Number.isNaN(d.getTime())) return "";
    return humanizePublishAt(d);
  }, [customDatetime]);

  const handleConfirm = () => {
    if (busy) return;
    if (selection === CUSTOM_ID) {
      const parsed = new Date(customDatetime);
      if (Number.isNaN(parsed.getTime())) {
        setCustomError("Please pick a valid date and time.");
        return;
      }
      const minMs = Date.now() + MIN_DELAY_MIN * 60_000;
      if (parsed.getTime() < minMs) {
        setCustomError(`Publish time must be at least ${MIN_DELAY_MIN} minutes from now.`);
        return;
      }
      onSchedule({ publish_at: parsed.toISOString() });
      return;
    }
    const preset = PRESET_OPTIONS.find((p) => p.id === selection);
    if (preset) {
      onSchedule({ publish_in_minutes: preset.minutes });
    }
  };

  const confirmDisabled = busy || selection == null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Schedule story idea</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {storyIdeaTitle && <p className="text-muted mb-3 fst-italic">&ldquo;{storyIdeaTitle}&rdquo;</p>}

        <div className="mb-2 fw-medium">Publish:</div>
        {/*
          Layout: four presets as a true segmented group (no gap,
          buttons touch, no wrap so the end-caps stay rounded).
          "Custom…" is rendered separately underneath as a non-
          segmented, slightly subordinate affordance — it opens a
          datetime input rather than picking a time directly, so it's
          a different kind of action.
        */}
        <ToggleButtonGroup
          type="radio"
          name="schedule-preset"
          value={selection}
          onChange={setSelection}
          className="d-flex mb-3"
        >
          {PRESET_OPTIONS.map((opt) => (
            <ToggleButton
              key={opt.id}
              id={opt.id}
              value={opt.id}
              variant={selection === opt.id ? "primary" : "outline-primary"}
              disabled={busy}
              onClick={() => handlePresetSelect(opt.id)}
            >
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <div className="mb-3">
          <Button
            variant={selection === CUSTOM_ID ? "primary" : "outline-secondary"}
            size="sm"
            disabled={busy}
            onClick={handleCustomSelect}
            aria-pressed={selection === CUSTOM_ID}
          >
            <i className="fa-solid fa-calendar me-1" aria-hidden="true" />
            Or schedule for a specific time&hellip;
          </Button>
        </div>

        {selection === CUSTOM_ID && (
          <div className="border rounded p-3 bg-light">
            <Form.Group controlId="schedule-modal-custom-datetime">
              <Form.Label>Publish at</Form.Label>
              <Form.Control
                type="datetime-local"
                value={customDatetime}
                onChange={(e) => {
                  setCustomDatetime(e.target.value);
                  setCustomError(null);
                }}
                autoFocus
                disabled={busy}
                isInvalid={Boolean(customError)}
              />
              {customError && <Form.Control.Feedback type="invalid">{customError}</Form.Control.Feedback>}
              {customPreview && !customError && <Form.Text className="text-muted">Publishes {customPreview}</Form.Text>}
              {!customPreview && !customError && (
                <Form.Text className="text-muted">Must be at least {MIN_DELAY_MIN} minutes from now.</Form.Text>
              )}
            </Form.Group>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={confirmDisabled}>
          <i className="fa-solid fa-paper-plane me-1" aria-hidden="true" />
          Schedule
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

ScheduleStoryIdeaModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  // Called with either { publish_in_minutes } or { publish_at }.
  // Parent fires the HTTP request and closes the modal on success.
  onSchedule: PropTypes.func.isRequired,
  // Optional — shown as a quoted subtitle in the modal so the admin
  // can confirm which story idea they're scheduling.
  storyIdeaTitle: PropTypes.string,
  // Optional — pre-fills the custom datetime when re-scheduling after
  // a revert. Forward-clamped to ≥ now + MIN_DELAY_MIN.
  previousPublishAt: PropTypes.string,
  // Disables actions while the parent's HTTP request is in flight.
  busy: PropTypes.bool,
};

ScheduleStoryIdeaModal.defaultProps = {
  storyIdeaTitle: null,
  previousPublishAt: null,
  busy: false,
};

export default ScheduleStoryIdeaModal;
